/**
 * Home Feature - Japanese (ja-JP)
 */

import type { HomeLocale } from './types';

const jaJP: HomeLocale = {
    home: {
        title: 'AI で特許作成を加速',
        subtitle: '発明開示書から拒絶理由通知への応答まで、特許文書作成プロセス全体をインテリジェントに完成',

        tabs: {
            patent: '特許出願',
            disclosure: '発明開示書',
            oar: '拒絶理由対応',
        },

        cardTitles: {
            patent: '特許出願',
            disclosure: '発明開示書',
            oar: '拒絶理由通知への応答',
        },

        placeholders: {
            patent: '技術ソリューション、解決する問題、革新的な側面を説明してください。\n\n{\'@\'} を入力してテンプレートを使用するか、ファイルをアップロードすることもできます。',
            disclosure: '発明のアイデア、解決する技術的問題、主要な革新点を説明してください。\n\n{\'@\'} を入力してテンプレートを使用するか、ファイルをアップロードすることもできます。',
            oar: '拒絶理由通知書の内容をここに貼り付けるか、対応が必要な拒絶理由を説明してください。',
        },

        placeholder: {
            default: '発明を説明するか、{\'@\'} を入力してテンプレートを使用',
            template: '{\'@\'} を入力して特許テンプレートを参照',
            example1: '例：画像認識のための機械学習システム...',
            example2: '例：高容量の改良型バッテリー設計...',
            example3: '例：{\'@\'} USPTO ソフトウェア特許テンプレート',
        },

        workMode: {
            agent: 'エージェント',
            copilot: 'コパイロット',
        },

        actions: {
            addFiles: 'ファイル追加',
            addFilesTooltip: 'ファイルをアップロード（PDF、Word、TXT）',
            readOnlyTip: '読み取り専用権限のため、ファイルをアップロードできません',
            dropToAdd: 'ドロップしてファイルを追加',
            style: 'スタイル',
            styleTooltip: '作成スタイルを選択',
            template: 'テンプレート',
            templateTooltip: '特許テンプレートを選択',
            voiceInput: '音声入力',
            voiceInputTooltip: '音声入力',
            voiceInputSoon: '音声入力は近日公開予定...',
            generate: '開始',
            loggingIn: 'ログイン中...',
            startDrafting: '作成開始',
            draftingStyle: '作成スタイル',
            selectTemplate: 'テンプレートを選択',
            removeFile: '削除',
            loginNow: 'ログイン',
            changeTemplate: 'クリックしてテンプレートを変更',
        },

        upload: {
            dragOrClick: 'ファイルをここにドラッグするか、クリックしてアップロード',
            supportedFormats: 'PDF、Word、TXT、画像ファイルに対応',
        },

        quickStart: {
            title: 'よく使う作成タスクでクイックスタート：',
            disclosure: '開示書作成',
            specification: '明細書作成',
            claims: 'クレーム作成',
            oar: '応答作成',
            figures: '図面設計',
        },

        messages: {
            filesAdded: '{count} 件のファイルを追加しました',
            pleaseEnterContent: '内容を入力するかファイルをアップロードしてください',
            pleaseUploadFile: '拒絶理由通知ファイルをアップロードしてください',
            generating: '特許文書を生成中...',
            navigationFailed: 'ナビゲーション失敗',
            styleApplied: 'スタイルを適用しました：{name}',
            templateApplied: 'テンプレートを選択しました：{name}',
            mentionApplied: '{type}を選択しました：{name}',
            templateLoading: 'テンプレートを読み込み中：{name}',
            submitFailed: '送信に失敗しました。もう一度お試しください',
            invalidFile: '無効なファイル',
            uploadFailed: 'ファイルのアップロードに失敗しました。もう一度お試しください',
            filesUploading: 'ファイルをアップロード中です。お待ちください',
            fileStatusUploading: 'アップロード中...',
            fileStatusFailed: 'アップロード失敗',
            someFilesNotUploaded: '一部のファイルのアップロードに失敗しました。再度アップロードしてください',
            emptyInput: '内容を入力するかファイルをアップロードしてください',
            emptyOarInput: '特許出願番号を入力するかファイルをアップロードしてください',
            fileProcessed: 'ファイル {name} の解析が完了しました',
            fileProcessFailed: 'ファイル {name} の解析に失敗しました',
            fileProcessingTimeout: 'ファイル解析がタイムアウトしました。再度アップロードしてください',
            filesProcessing: 'ファイルを解析中です。お待ちください...',
            filesNotReady: '一部のファイルの解析が完了していません。完了後にタスクを作成してください',
            fileAlreadyExists: 'ファイル {name} は既に存在します。既存のファイルを使用します',
            defaultPromptForPatent: 'アップロードされた文書を解析し、技術開示書分析レポートと特許出願書類を生成してください。',
            defaultPromptForDisclosure: '発明開示書分析の標準フローに従ってファイルを処理し、発明開示書を生成してください',
            loginRequired: '作成を開始するにはログインしてください。作業はアカウントに保存されます。',
        },

        // タスクタイプとシナリオに基づいて自動選択される統一された初期プロンプト設定
        initialPrompts: {
            patent_draft: {
                withFiles: 'アップロードされた文書を解析し、技術開示書分析レポートと特許出願書類を生成してください。',
                empty: '特許出願の作成を開始してください',
            },
            disclosure: {
                withFiles: '発明開示書分析の標準フローに従ってファイルを処理し、発明開示書を生成してください',
                empty: '発明開示書の作成を開始してください',
            },
            prosecution: {
                withPatentNumber: '拒絶理由通知の分析を開始（出願番号：{patentNumber}）',
                withFiles: '拒絶理由通知の分析を開始',
                empty: '拒絶理由通知を分析し、応答を生成してください',
            },
        },

        mention: {
            title: 'テンプレート',
            selectType: 'タイプを選択',
            styleTitle: 'スタイル',
            priorArt: '先行技術',
            file: 'ファイル',
            document: 'ドキュメント',
            loading: '読み込み中...',
            navigate: 'ナビゲート',
            select: '選択',
            close: '閉じる',
            noResults: '"{query}" に一致するテンプレートが見つかりません',
            tryDifferent: '別の検索語を試してください',
            applicationDate: '出願:',
            publicationDate: '公開:',
            docTypes: {
                patent_draft: '特許作成',
                disclosure: '技術開示書',
                prosecution: '審査意見対応',
            },
            docStatus: {
                draft: '下書き',
                in_review: 'レビュー中',
                approved: '承認済み',
                filed: '提出済み',
            },
            types: {
                template: {
                    label: 'テンプレート',
                    desc: '特許出願テンプレート',
                },
                style: {
                    label: '作成スタイル',
                    desc: '作成スタイル設定',
                },
                priorArt: {
                    label: '先行技術',
                    desc: '先行技術特許',
                },
                file: {
                    label: 'ファイル',
                    desc: '添付ファイル',
                },
                document: {
                    label: 'ドキュメント',
                    desc: 'プロジェクトドキュメント',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO 実用特許',
                usptoSoftwarePatent: 'USPTO ソフトウェア特許',
                epoPatentApplication: 'EPO 特許出願',
                pctInternationalApplication: 'PCT 国際出願',
                cnipaPatentApplication: 'CNIPA 特許出願',
            },
        },

        tooltips: {
            uploadFile: 'ファイルをアップロード',
            voiceInput: '音声入力',
        },

        patent: {
            jurisdiction: '出願地域を選択してください',
        },

        oar: {
            title: '拒絶理由通知への応答',
            projectTitle: '拒絶理由通知への応答 - {patentNumber}',
            projectTitleWithoutNumber: '拒絶理由通知への応答',
            initialPrompt: '拒絶理由通知の分析を開始',
            initialPromptWithNumber: '拒絶理由通知の分析を開始（出願番号：{patentNumber}）',
            confirmMessage: 'OAR analysis results confirmed, please continue processing',
            oarExtractList: 'OAR抽出リスト',
            rejectionTableTitle: '拒絶分析表',
            rejectionAnalysis: {
                title: '拒絶理由分析',
                status: {
                    extracting: 'データ抽出中',
                    processing: '処理中',
                },
                button: {
                    confirm: '確認して続行',
                    saving: '保存中',
                    addClause: '条項を追加',
                    showMore: 'もっと見る',
                    showLess: '折りたたむ',
                    export: 'Excelエクスポート',
                    exportSheetName: '拒絶理由分析',
                    viewDetail: '論理チェーンを見る',
                    delete: '削除',
                },
                detail: {
                    drawerTitle: '分析詳細',
                    examinerOpinion: '審査官意見',
                    featureComparison: '特徴比較',
                    claimFeature: '本願特徴',
                    priorArtFeature: '引用文献特徴',
                    priorArtLabel: '引用文献{no} · {name}',
                    noPriorArt: '引用文献なし',
                    strategy: '応答戦略',
                    strategyReason: '戦略根拠',
                    prevItem: '前へ',
                    nextItem: '次へ',
                },
                basicInfo: {
                    title: '基本情報',
                    application: '出願番号',
                    oaNo: '拒絶理由通知番号',
                    type: 'タイプ',
                    examiner: '審査官',
                    issueDate: '発行日',
                    priorArt: '引用文献',
                    applicationFiles: '出願書類',
                },
                tabs: {
                    all: 'すべて',
                },
                tableHeaders: {
                    claim: 'クレーム',
                    statute: '法令',
                    claimAndStatute: 'クレーム/法令',
                    claimElement: 'クレーム要素',
                    examinerReasoning: '審査官の理由',
                    aiAnalysis: 'AI分析',
                    strategy: '応答戦略',
                },
                statusPills: {
                    agree: '同意',
                    unsure: '不明',
                    disagree: '不同意',
                },
                direction: {
                    agree: '同意',
                    unsure: '不明',
                    disagree: '不同意',
                },
                labels: {
                    direction: '応答方向',
                    strategy: '応答戦略',
                    claimAmendment: 'クレーム補正',
                    amendmentMethod: '補正方法',
                    amendmentBasis: '補正根拠',
                },
                needAmendment: {
                    yes: '補正',
                    no: '補正なし',
                    optional: 'AI判断',
                },
                tooltips: {
                    direction: '審査官の見解に同意するかどうか',
                    strategy: 'この拒絶理由に対する応答戦略',
                    claimAmendment: 'クレームの補正が必要かどうか',
                },
                placeholder: {
                    edit: 'クリックして編集...',
                    claimElement: 'クレーム要素を入力...',
                    examinerReasoning: '審査官の意見を入力...',
                    strategy: '応答戦略を入力...',
                    amendmentMethod: '補正方法を入力...',
                    amendmentBasis: '補正根拠を入力...',
                },
                emptyState: {
                    noData: '拒絶理由データなし',
                    hint: '抽出完了後、ここに拒絶理由分析が表示されます',
                },
                exportHeaders: {
                    claim: 'クレーム',
                    statute: '法令',
                    claimElement: 'クレーム要素',
                    examinerReasoning: '審査官の意見',
                    direction: '応答方向',
                    strategy: '応答戦略',
                    distinguishingFeatures: '区別技術特徴',
                    priorArt: '引用文献',
                    needAmendment: '補正要否',
                    amendmentMethod: '補正方法',
                    amendmentBasis: '補正根拠',
                },
                message: {
                    noDataId: 'データIDを取得できません',
                    noData: 'データがありません',
                    saveSuccess: '保存しました',
                    saveFailed: '保存に失敗しました',
                    deleteSuccess: '削除しました',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
            },
            patentNumber: {
                label: '特許出願番号',
                placeholder: '審査中の特許の出願番号を入力して、最新の審査意見を取得',
            },
            uploadAction: {
                title: '拒絶理由通知をアップロード',
                choose: 'ファイルを選択',
                description: '回答が必要な審査意見のPDF/Wordファイルをアップロードしてください',
                uploading: 'アップロード中',
                addMore: 'さらにファイルをアップロード',
            },
            uploadedFiles: {
                title: 'アップロード済みファイル ({count})',
            },
            fileStatus: {
                preparing: 'ファイル準備中...',
                analyzing: '内容分析中...',
                finalizing: 'もうすぐ完了...',
                processing: '処理中...',
                estimatedTime: '推定 1-2 分',
                completed: '完了',
                failed: '解析失敗',
            },
            actions: {
                agent: 'エージェント',
                copilot: 'コパイロット',
                start: '開始',
            },
            search: {
                failed: '特許検索に失敗しました。もう一度お試しください',
                notFound: '特許が見つかりません',
            },
            patent: {
                publicationNumber: '公開番号：',
                publicationDate: '公開日：',
                currentApplicant: '[現在の]出願人：',
            },
            download: {
                searching: '拒絶理由通知PDFを検索中...',
                downloading: 'PDFダウンロードを開始しました。ブラウザのダウンロードを確認してください',
                success: 'PDFダウンロード成功',
                failed: 'PDFダウンロードに失敗しました。もう一度お試しください',
                notFound: '拒絶理由通知PDFが見つかりません。手動でアップロードできます',
                visitUsptoWebsite: 'USPTO Webサイトにアクセスしてダウンロード',
            },
            unsupportedRegion: 'この地域はまだサポートされていません',
            prosecutionTip: '特許出願番号を入力/審査意見PDFをアップロードして開始',
            rejectionDetail: {
                drawerTitle: '拒絶理由詳細',
                examinerOpinion: '審査官意見',
                featureComparison: '特徴比較',
                claimFeature: 'クレーム特徴',
                specificationSource: '明細書原文',
                priorArtFeature: '引用文献特徴',
                priorArtLabel: '引用文献{no}（{name}）',
                noPriorArt: '引用文献情報がありません',
                priorArtSource: '{name} 原文',
                distinguishingFeatures: '区別技術特徴',
                aiStrategy: 'AI 応答戦略',
                strategyReason: '戦略根拠',
                crossValidation: '交差検証',
                prevItem: '前へ',
                nextItem: '次へ',
                direction: {
                    agree: '同意',
                    unsure: '保留',
                    disagree: '不同意',
                },
                claimAmendment: {
                    title: 'クレーム補正提案',
                    needAmendment: '補正要否',
                    yes: '補正',
                    no: '補正なし',
                    optional: 'AI判断',
                    method: '補正方法',
                    basis: '補正根拠',
                },
            },
        },

        draftingStyle: {
            title: '作成スタイルを選択',
            stylesHeader: 'スタイル',
            previewHeader: 'プレビュー',
            cancel: 'キャンセル',
            apply: 'スタイルを適用',
            loading: '読み込み中...',
            loadingPreview: 'プレビュー読み込み中...',
            loadError: 'スタイル一覧の読み込みに失敗しました',
            noStyles: '利用可能なスタイルがありません',
            noPreview: 'プレビューがありません',
            pleaseSelect: 'まずスタイルを選択してください',
            used: '{count}回使用',
            styles: {
                standard: {
                    name: '標準フォーマット',
                    description: '番号付き段落と正式な言語を使用した従来の特許フォーマット',
                },
                detailed: {
                    name: '詳細技術',
                    description: '詳細な実施例を含む包括的な技術説明',
                },
                concise: {
                    name: '簡潔クレーム重視',
                    description: '明確で強力なクレーム言語を強調した簡潔なフォーマット',
                },
                international: {
                    name: 'PCT 国際出願',
                    description: 'PCT に基づく国際特許出願に最適化されたフォーマット',
                },
                continuation: {
                    name: '継続出願',
                    description: '親出願を参照する継続出願または分割出願のフォーマット',
                },
            },
        },

        templateSelection: {
            title: 'テンプレートを選択',
            templatesHeader: 'テンプレート一覧',
            previewHeader: 'プレビュー',
            cancel: 'キャンセル',
            apply: 'テンプレートを適用',
            loading: '読み込み中...',
            loadingPreview: 'プレビューを読み込み中...',
            loadError: 'テンプレート一覧の読み込みに失敗しました',
            loadDetailError: 'テンプレート詳細の読み込みに失敗しました',
            noTemplates: '利用可能なテンプレートがありません',
            noPreview: 'プレビューがありません',
            pleaseSelect: '最初にテンプレートを選択してください',
        },

        documentModifications: {
            title: '文書変更を適用済み',
            summary: '合計 {count} 件の変更',
            streamingSummary: '変更を生成中、{count} 件を検出',
            streamingTitle: 'AI が文書を編集中です',
            streamingDesc: '変更内容を受信中です。完了後、バックエンドでマージして最新バージョンに更新します。',
            streamPreview: 'リアルタイム変更ストリーム',
            appliedSummary: '{count} 件の変更を適用済み',
            appliedTitle: '変更は自動的に適用されました',
            appliedDesc: 'バックエンドで編集をマージし、新しいバージョンを作成しました。エディターは最新内容に更新されます。',
            emptyState: '変更詳細がありません',
            noContent: '新しい内容はありません',
            oldContent: '旧テキスト:',
            actions: {
                insert: '挿入',
                replace: '置換',
                delete: '削除',
            },
            tableHeaders: {
                index: '#',
                documentId: '文書 ID',
                action: '操作',
                dataId: 'data-id',
                content: '変更内容',
                oldContent: '旧テキスト',
            },
            exportExcel: 'Excelエクスポート',
        },
    },
};

export default jaJP;
